import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pharmacyAPI } from '../../utils/api';
import { toast } from 'react-toastify';

export default function PharmacyHistory() {
    const navigate = useNavigate();
    const [history, setHistory] = useState([]);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await pharmacyAPI.getHistory({ date });
            setHistory(res.data);
        } catch { toast.error('Failed to load history'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [date]);

    const handlePrintDetail = async (id) => {
        try {
            const res = await pharmacyAPI.getDispensingDetail(id);
            const { dispensing, items } = res.data;
            const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
            <style>
                body { font-family: Arial, sans-serif; padding: 20px; font-size: 13px; }
                .header { text-align: center; border-bottom: 2px solid #1e3a5f; padding-bottom: 10px; margin-bottom: 14px; }
                .rx-symbol { font-size: 32px; color: #1e3a5f; font-style: italic; font-weight: bold; }
                h2 { color: #1e3a5f; margin: 4px 0; font-size: 18px; }
                .info { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 14px; font-size: 12px; }
                .info span { color: #64748b; }
                table { width: 100%; border-collapse: collapse; }
                th { background: #1e3a5f; color: white; padding: 7px 10px; font-size: 12px; text-align: left; }
                td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
                .total { text-align: right; font-size: 14px; font-weight: bold; color: #1e3a5f; margin-top: 10px; }
                .footer { margin-top: 20px; font-size: 10px; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 10px; }
                @media print { @page { size: A5; margin: 10mm; } }
            </style></head><body>
            <div class="header">
                <div class="rx-symbol">℞</div>
                <h2>HIMS Pharmacy</h2>
                <p style="font-size:12px;color:#64748b;margin:2px 0">Prescription Slip — ${dispensing.dispensing_number}</p>
                <p style="font-size:11px;color:#94a3b8;margin:2px 0">${new Date(dispensing.dispensed_at).toLocaleString('en-PK')}</p>
            </div>
            <div class="info">
                <div><span>Patient: </span><strong>${dispensing.full_name}</strong></div>
                <div><span>MRN: </span><strong>${dispensing.mrn}</strong></div>
                <div><span>Age/Gender: </span>${dispensing.age_years}y / ${dispensing.gender}</div>
                <div><span>Source: </span>${dispensing.prescription_source}</div>
                <div><span>Dispensed by: </span>${dispensing.dispensed_by_name || '—'}</div>
            </div>
            <table>
                <tr><th>#</th><th>Medicine</th><th>Qty</th><th>Instructions</th><th>Amount</th></tr>
                ${items.map((item, i) => `
                <tr><td>${i+1}</td><td><strong>${item.drug_name}</strong></td><td>${item.quantity} ${item.unit}</td><td>${item.dose_instructions || '—'}</td><td>Rs. ${parseFloat(item.total_price).toFixed(2)}</td></tr>`).join('')}
            </table>
            <div class="total">Total: Rs. ${parseFloat(dispensing.total_amount).toFixed(2)}</div>
            ${dispensing.notes ? `<p style="font-size:12px;color:#475569;margin-top:10px;"><strong>Notes:</strong> ${dispensing.notes}</p>` : ''}
            <div class="footer">HIMS — Hospital Information Management System — Keep this slip for your records</div>
            </body></html>`;
            const win = window.open('', '_blank', 'width=700,height=600');
            if (!win) return alert('Popup blocked!');
            win.document.write(html);
            win.document.close();
            setTimeout(() => win.print(), 600);
        } catch { toast.error('Failed to load detail'); }
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={() => navigate('/pharmacy')} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}>← Back</button>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>📋 Dispensing History</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Past dispensing records</p>
                    </div>
                </div>
                <input type="date" value={date} onChange={e => setDate(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }} />
            </div>

            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc' }}>
                            {['Rx No.', 'Patient', 'MRN', 'Source', 'Total', 'Time', 'Dispensed By', 'Print'].map(h => (
                                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</td></tr>
                        ) : history.length === 0 ? (
                            <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No dispensing records for this date</td></tr>
                        ) : history.map(h => (
                            <tr key={h.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#1e3a5f' }}>{h.dispensing_number}</td>
                                <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600 }}>{h.full_name}</td>
                                <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{h.mrn}</td>
                                <td style={{ padding: '10px 14px' }}>
                                    <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{h.prescription_source}</span>
                                </td>
                                <td style={{ padding: '10px 14px', fontWeight: 700, fontSize: 13, color: '#059669' }}>Rs. {parseFloat(h.total_amount).toFixed(2)}</td>
                                <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{new Date(h.dispensed_at).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}</td>
                                <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{h.dispensed_by_name || '—'}</td>
                                <td style={{ padding: '10px 14px' }}>
                                    <button onClick={() => handlePrintDetail(h.id)}
                                        style={{ padding: '4px 10px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                        🖨️
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
